import { Badge, Button, Progress, Select, Stack, Text } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";

import { http } from "../../api/http";
import { useCan } from "../../auth/user";
import { DataTable } from "../../components/DataTable";
import { ReportButton } from "../../components/ReportButton";
import { fmtNum, KpiGrid, PageHeader, SEASONS } from "../../components/ui";
import { useT } from "../../i18n/script";

export default function PhenologyOverviewPage() {
  const { t } = useT();
  const can = useCan();
  const nav = useNavigate();
  const [season, setSeason] = useState(String(new Date().getFullYear()));
  const q = useQuery({ queryKey: ["pheno", "compliance", season],
    queryFn: () => http.get<Record<string, number | string>[]>("/api/v1/phenology/compliance", { season }) });
  const rows = q.data ?? [];
  const done = rows.reduce((s, r) => s + Number(r.mandatory_done), 0), tot = rows.reduce((s, r) => s + Number(r.mandatory_total), 0);
  return (
    <Stack>
      <PageHeader title="Fenologija" description="Ispunjenost obaveznih fenoloških faza (BBCH) po voćnjacima. Faze i obavezni podaci se definišu u šablonima; proizvođač može dodati sopstvene faze i polja na nivou voćnjaka."
        right={<>
          <Select w={100} data={SEASONS} value={season} onChange={(v) => v && setSeason(v)} />
          <ReportButton code="fenologija_sezona" params={{ sezona: Number(season) }} label="Izvještaj" />
          {can("phenology:read") && <Button variant="default" onClick={() => nav("/admin/fenologija")}>{t("Šabloni faza")}</Button>}
        </>} />
      <KpiGrid items={[
        { label: "Voćnjaka", value: rows.length },
        { label: "Obavezne faze upisane", value: `${fmtNum(tot ? (100 * done) / tot : 0, 0)} %`, hint: `${done} / ${tot}` },
        { label: "Voćnjaka sa fazama koje kasne", value: rows.filter((r) => Number(r.overdue) > 0).length, color: "red" },
        { label: "Potpuno ispunjeno", value: rows.filter((r) => Number(r.mandatory_done) >= Number(r.mandatory_total)).length, color: "green" },
      ]} />
      <DataTable rows={rows} exportName={`fenologija_${season}`} onRowClick={(r) => nav(`/vocnjaci/${r.id}`)} columns={[
        { key: "name", label: "Voćnjak" }, { key: "species", label: "Vrsta" }, { key: "producer", label: "Proizvođač" }, { key: "opstina", label: "Opština" },
        { key: "ispunjenost", label: "Ispunjenost", render: (r) => {
          const p = (100 * Number(r.mandatory_done)) / Math.max(1, Number(r.mandatory_total));
          return <Stack gap={2}><Text size="xs">{String(r.mandatory_done)}/{String(r.mandatory_total)}</Text><Progress size="sm" value={p} color={p >= 100 ? "green" : p > 50 ? "yellow" : "red"} /></Stack>;
        } },
        { key: "overdue", label: "Kasni", type: "integer", render: (r) => (Number(r.overdue) > 0 ? <Badge color="red" size="sm">{String(r.overdue)}</Badge> : "—") },
      ]} />
    </Stack>
  );
}
